import { isValid, parseISO } from 'date-fns';

import { StorageService } from '~/services/StorageService/StorageService';

import { DEFAULT_LOCALE, LANGUAGE_KEY, LOCALE_KEY } from './constants';

type OriginalFormat = Record<
  string,
  {
    comment?: string;
    value: string;
  }
>;

type TranslationRecord = Record<string, string>;

export function transformToLocaleObject(
  input: OriginalFormat,
): TranslationRecord {
  const result: TranslationRecord = {};

  for (const key in input) {
    const entry = input[key];
    result[key] = entry.value;
  }

  return result;
}

export function parseUserDate(date: string | Date | number | null | undefined) {
  if (!date) return null;

  const isISOString = (value: any): boolean => {
    if (typeof value !== 'string') return false;

    try {
      const parsed = parseISO(value);
      return isValid(parsed);
    } catch {
      return false;
    }
  };

  const isUTCMidnight = (value: string): boolean => {
    return (
      parseISO(value).getUTCHours() === 0 &&
      parseISO(value).getUTCMinutes() === 0 &&
      parseISO(value).getUTCSeconds() === 0
    );
  };

  const parsedDate =
    date instanceof Date
      ? date // Keep Date objects as-is
      : typeof date === 'number'
        ? new Date(date) // Handle timestamps correctly
        : isISOString(date)
          ? /** Midnight UTC timestamps (T00:00:00Z) should be treated as date-only values
             *  This ensures consistent date display across all timezones without day-shifting artifacts.
             *  This is a workaround to avoid the timezone offset.
             *  It will require parsing as noon local time to avoid the timezone offset when turning into Date objects.*/
            isUTCMidnight(date)
            ? parseISO(date.split('T')[0] + 'T12:00:00')
            : parseISO(date)
          : isValid(new Date(date))
            ? new Date(date)
            : null; // Fallback for invalid date
  return parsedDate;
}

export function isBrowserSupportedUnit(unit: string): boolean {
  /**
   * Units that are supported by the browser's Intl.NumberFormat
   * These get automatic translation/localization by the browser
   */
  const browserSupportedUnits = [
    // Length
    'meter',
    'kilometer',
    'centimeter',
    'millimeter',
    'inch',
    'foot',
    'yard',
    'mile',
    // Mass
    'gram',
    'kilogram',
    'pound',
    'ounce',
    'stone',
    // Time
    'second',
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year',
    // Temperature
    'celsius',
    'fahrenheit',
    'kelvin',
    // Volume
    'liter',
    'milliliter',
    'gallon',
    'fluid-ounce',
    'cup',
    'pint',
    'quart',
    // Digital
    'byte',
    'kilobyte',
    'megabyte',
    'gigabyte',
    'terabyte',
    'petabyte',
    'bit',
    'kilobit',
    'megabit',
    'gigabit',
    'terabit',
    'petabit',
    // Frequency
    'hertz',
    'kilohertz',
    'megahertz',
    'gigahertz',
    // Energy
    'joule',
    'kilojoule',
    'calorie',
    'kilocalorie',
    // Power
    'watt',
    'kilowatt',
    'horsepower',
    // Pressure
    'pascal',
    'hectopascal',
    'atmosphere',
    'bar',
    // Speed
    'meter-per-second',
    'kilometer-per-hour',
    'mile-per-hour',
    // Area
    'square-meter',
    'square-kilometer',
    'square-foot',
    'square-mile',
    'hectare',
    'acre',
    // Electric
    'ampere',
    'milliampere',
    'volt',
    'millivolt',
    // Percent (special case)
    'percent',
  ];

  return browserSupportedUnits.includes(unit.toLowerCase());
}

/**
 * Gets the formatting locale from localStorage.
 * Used by formatting hooks to determine date/number formatting locale independently from translation language.
 *
 * @returns The locale string for formatting (e.g., 'en-CA', 'fr-FR') or falls back to navigator.language or 'en'
 */
export function getFormattingLocale(): string {
  const stored = StorageService.getItem(LOCALE_KEY);
  if (stored && stored !== 'null' && stored !== 'system-default') {
    return stored;
  }
  return StorageService.getItem(LANGUAGE_KEY) ?? DEFAULT_LOCALE;
}
